import {
  Button,
  Form,
  FormProps,
  Input,
  message,
  Modal,
  notification,
  Select,
  Tooltip,
} from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DeliverableService from "../../../features/DeliverableView/services/DeliverableService";
import { MODAL_STYLES, MODAL_TITLE_STYLE } from "../../../styles/modalStyles";
import {
  CANCEL_TEXT,
  CREATE_DELIVERABLE_BUTTON_LABEL,
  CREATE_NEW_DELIVERABLE_LABEL,
  CREATE_TEXT,
  DELIVERABLE_CREATED,
  DELIVERABLE_NAME_LABEL,
  ENTER_DELIVERABLE_NAME,
  ENTER_DELIVERABLE_NAME_MESSAGE,
  FILTER_COA_LABEL,
  FILTER_TAGS_LABEL,
  SELECT_COA,
  SELECT_TAG,
  SELECT_TEMPLATE,
  SELECT_TEMPLATE_MESSAGE,
  TOAST_DEFAULT_ERROR_MESSAGE,
} from "../../utils/CommonConstants";
import { DeliverableFilter } from "../../../features/DeliverableView/pages/DeliverableList/DeliverableList.types";

interface DeliverableCreateProps {
  tagOptions: any[];
  filterOptions: DeliverableFilter;
  tags: any;
  deliverableService: DeliverableService;
  fetchData: (currentPage: any, pageSize: any) => {};
}

export const DeliverableCreate = ({
  tagOptions,
  filterOptions,
  tags,
  deliverableService,
  fetchData,
}: DeliverableCreateProps) => {
  const [form] = Form.useForm();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeliverableCreated, setIsDeliverableCreated] = useState(false);

  const [selectedTags, setSelectedTags] = useState<any[]>([]);

  const navigate = useNavigate();

  const handleChange = (value: any) => {
    setSelectedTags(value);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    form.resetFields();
    setSelectedTags([]);
  };

  const createDeliverable: FormProps<any>["onFinish"] = async (values) => {
    setIsDeliverableCreated(true);

    try {
      const tag = selectedTags.map((tag) => {
        return {
          id: tagOptions.find((t: any) => t.label === tag)?.id,
          value: values[tag.toCamelCase()],
        };
      });

      const ruleEngineId = filterOptions.ruleEngineTemplateList.find(
        (template: any) => template.id === values.template
      )!.ruleEngineId;

      const newDeliverable = {
        name: values.deliverableName,
        ruleEngineId,
        coaId: values.coa ?? null,
        tag,
      };

      const id: number = await deliverableService.createDeliverable(
        newDeliverable
      );
      message.success(DELIVERABLE_CREATED);
      fetchData(0, 10);
      closeModal();

      if (id !== undefined) {
        navigate(`/deliverable/${id}`);
      }
      form.resetFields();
      setIsModalOpen(false);
      setIsDeliverableCreated(false);
    } catch (error) {
      notification.error({ message: TOAST_DEFAULT_ERROR_MESSAGE });
      setIsDeliverableCreated(false);
    }
  };

  return (
    <>
      <Button
        id="create-deliverable"
        type="primary"
        onClick={() => setIsModalOpen(true)}
      >
        {CREATE_DELIVERABLE_BUTTON_LABEL}
      </Button>
      <Modal
        title={<span style={MODAL_TITLE_STYLE}>{CREATE_NEW_DELIVERABLE_LABEL}</span>}
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        styles={MODAL_STYLES}
      >
        <Form
          className="create-deliverable-form"
          form={form}
          onFinish={createDeliverable}
          layout="vertical"
        >
          <Form.Item
            name="deliverableName"
            label={DELIVERABLE_NAME_LABEL}
            required
            rules={[
              {
                required: true,
                message: ENTER_DELIVERABLE_NAME_MESSAGE,
              },
            ]}
          >
            <Input placeholder={ENTER_DELIVERABLE_NAME} />
          </Form.Item>
          <Form.Item
            name="template"
            label="Template"
            required
            rules={[
              {
                required: true,
                message: SELECT_TEMPLATE_MESSAGE,
              },
            ]}
          >
            <Select
              placeholder={SELECT_TEMPLATE}
              options={filterOptions.ruleEngineTemplateList.map(
                (template: any) => ({
                  label: template.templateName,
                  value: template.id,
                })
              )}
              showSearch
            ></Select>
          </Form.Item>
          <Form.Item name="coa" label={FILTER_COA_LABEL}>
            <Select placeholder={SELECT_COA}>
              {filterOptions.coaTemplateList.map((coa: any) => (
                <Select.Option key={coa.id} value={coa.coaId}>
                  {coa.templateName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label={FILTER_TAGS_LABEL}>
            <Select
              allowClear
              placeholder={SELECT_TAG}
              mode="multiple"
              onChange={handleChange}
              value={selectedTags}
              maxTagCount={1}
              maxTagPlaceholder={(omittedValues) => (
                <Tooltip
                  overlayStyle={{ pointerEvents: "none" }}
                  title={omittedValues.map(({ label }) => label).join(", ")}
                >
                  <span>+ {omittedValues.length} ...</span>
                </Tooltip>
              )}
            >
              {tagOptions.map((tag: any) => (
                <Select.Option key={tag.label} value={tag.label}>
                  {tag.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {selectedTags.map((tag: string) => (
            <Form.Item
              name={tag.toCamelCase()}
              label={tag}
              rules={[
                {
                  required: true,
                  message: "Please select " + tag,
                },
              ]}
            >
              <Select placeholder={"Select " + tag}>
                {tags[tag].map((value: string) => (
                  <Select.Option key={value} value={value}>
                    {value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ))}
          <div className="w-full flex items-center gap-3 footer">
            <Button
              id="cancel-create-deliverable"
              type="default"
              block
              onClick={closeModal}
            >
              {CANCEL_TEXT}
            </Button>
            <Button
              id="submit-create-deliverable"
              type="primary"
              block
              htmlType="submit"
              loading={isDeliverableCreated}
            >
              {CREATE_TEXT}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};
